<template>
  <div>
    <b-overlay :show="isProcessing">
      <div class="p-3">
        <h5 class="mb-2">
          {{ $t('Manage Tags for') }} {{ user.name }}
        </h5>

        <label class="d-block mb-1">{{ $t('Tags') }}</label>

        <v-select
          v-model="selected"
          :options="tagOptions"
          label="name"
          multiple
          clearable
          :close-on-select="false"
        >
          <template #option="option">
            <div class="d-flex align-items-center">
              <tag-badge
                :name="option.name"
                :color="option.color"
                :description="option.description || ''"
                size="small"
              />
              <span class="ml-2">{{ option.name }}</span>
            </div>
          </template>

          <template #selected-option="option">
            <div class="d-inline-flex align-items-center">
              <tag-badge
                :name="option.name"
                :color="option.color"
                :description="option.description || ''"
                size="small"
              />
            </div>
          </template>
        </v-select>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            @click="$emit('cancel')"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            class="ml-2"
            variant="primary"
            @click="save"
          >
            {{ $t('Save') }}
          </b-button>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BButton, BOverlay } from 'bootstrap-vue'
import vSelect from 'vue-select'
import useApollo from '@/plugins/graphql/useApollo'
import TagBadge from '@/views/Settings/UserTags/Components/TagBadge.vue'

export default {
  name: 'AssignUserTags',
  components: {
    vSelect, BButton, BOverlay, TagBadge,
  },
  props: {
    user: { type: Object, required: true },
  },
  data() {
    return {
      tagOptions: [],
      selected: [],
      isProcessing: false,
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.isProcessing = true
      const projectId = this.$store.state.project.selectedProject
      useApollo.project
        .getProjectTags({ projectId, first: -1 })
        .then(res => {
          const tags = res && res.data && res.data.project && res.data.project.tags && res.data.project.tags.data
            ? res.data.project.tags.data
            : []
          this.tagOptions = tags.map(t => ({
            id: t.id, name: t.name, color: t.color, description: t.description || '',
          }))
          // prepare selected from user.tags (if present) - match full option objects
          const userTagIds = (this.user.tags || []).map(t => t.id)
          this.selected = this.tagOptions.filter(opt => userTagIds.includes(opt.id))
        })
        .catch(err => console.error('Error loading tags', err))
        .finally(() => {
          this.isProcessing = false
        })
    },
    save() {
      this.isProcessing = true
      const projectUid = this.$store.state.project.selectedProject
      // Extract IDs from selected option objects
      const tagIds = this.selected.map(opt => opt.id)
      useApollo.project
        .assignTagsToUser({ projectUid, userUid: this.user.uuid, tagIds })
        .then(res => {
          // Use the project's standardized success helper
          this.showSuccessMessage({ data: { message: this.$t('Tags saved') || 'Tags saved' } })
          this.$emit('saved')
        })
        .catch(err => {
          this.showErrorMessage(err)
        })
        .finally(() => {
          this.isProcessing = false
        })
    },
  },
}
</script>

<style scoped>
/* Non-scoped styles to ensure TagBadge renders properly inside v-select */
.vs__dropdown-option .tag-badge:before,
.vs__selected-options .tag-badge:before {
  content: "";
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 12px solid var(--tag-bg);
  z-index: 0;
}

.vs__dropdown-option .tag-hole,
.vs__selected-options .tag-hole {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.32) inset;
  border: 2px solid rgba(0, 0, 0, 0.08);
  z-index: 2;
}

/* Remove the blue/primary background from selected items */
.vs__selected {
  min-height: 38px;

  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 5px 5px 2px 10px !important;
}
</style>
